import {useCallback, useRef, useState} from "react";
import useInterval from "../../../Shared/Hooks/useInterval";
import {LocalDateTimeFormatISO} from "../../../Utils/DateFormatPatternUtils";

export type DailyOverviewRefreshPeriod = {
    startDateTime: string;
    endDateTime: string;
    dayKey: string;
};

const buildTodayPeriod = (): DailyOverviewRefreshPeriod => {
    const toDateTime = new Date();
    const fromDateTime = new Date();
    fromDateTime.setHours(0, 0, 0, 0);
    const dayKey = fromDateTime.toISOString().slice(0, 10);
    return {
        startDateTime: LocalDateTimeFormatISO(fromDateTime),
        endDateTime: LocalDateTimeFormatISO(toDateTime),
        dayKey,
    };
};

export function useDailyOverviewRefresh(options: {
    enabled: boolean;
    intervalMs: number;
    onCycle?: (period: DailyOverviewRefreshPeriod, refreshGeneration: number) => void;
}) {
    const {enabled, intervalMs, onCycle} = options;
    const [refreshGeneration, setRefreshGeneration] = useState(0);
    const [period, setPeriod] = useState<DailyOverviewRefreshPeriod>(() => buildTodayPeriod());
    const cycleBusyRef = useRef(false);
    const refreshGenerationRef = useRef(0);

    const runCycle = useCallback(() => {
        if (!enabled || cycleBusyRef.current) {
            return;
        }
        cycleBusyRef.current = true;
        const nextPeriod = buildTodayPeriod();
        const nextGeneration = refreshGenerationRef.current + 1;
        refreshGenerationRef.current = nextGeneration;
        setPeriod(nextPeriod);
        setRefreshGeneration(nextGeneration);
        onCycle?.(nextPeriod, nextGeneration);
        cycleBusyRef.current = false;
    }, [enabled, onCycle]);

    useInterval(runCycle, enabled ? intervalMs : 86_400_000);

    return {
        refreshGeneration,
        period,
        runCycle,
    };
}
